import { useNavigate, useParams } from 'react-router-dom';

import { CareerPathScoreSection, ColorToken } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';

import { Chip, SxProps, Theme } from '@mui/material';

type PillColors = Readonly<{
  bg: ColorToken;
  border: ColorToken;
  text: ColorToken;
  hoverBg: ColorToken;
}>;

const FIT_COLORS: Record<CareerPathScoreSection, PillColors> = {
  TOP: { bg: 'Brand/100', border: 'Brand/300', text: 'Brand/900', hoverBg: 'Brand/200' },
  MIDDLE: { bg: 'Brand/50', border: 'Brand/200', text: 'Brand/700', hoverBg: 'Brand/100' },
  LOW: { bg: 'GrayBlue/50', border: 'GrayBlue/200', text: 'GrayBlue/700', hoverBg: 'GrayBlue/100' },
};

const SOLID_COLORS: PillColors = {
  bg: 'Brand/600',
  border: 'Colors/Base/Black',
  text: 'Colors/Base/White',
  hoverBg: 'Brand/700',
};

type CareerPathPillProps = Readonly<{
  id: number;
  name: string;
  scoreSection: CareerPathScoreSection;
  isShared?: boolean;
  /** When false, the pill does not navigate on click. Defaults to true. */
  interactive?: boolean;
  /** 'fit' (default) varies color by scoreSection; 'solid' uses a single brand-600 style. */
  variant?: 'fit' | 'solid';
}>;

export function CareerPathPill({
  id,
  name,
  scoreSection,
  isShared = false,
  interactive = true,
  variant = 'fit',
}: CareerPathPillProps) {
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();

  const path =
    isShared && uuid
      ? `${APP_ROUTE.SHARED_CAREER_PATHS.replace(':uuid', uuid)}/${id}`
      : `${APP_ROUTE.CAREER_PATHS}/${id}`;
  const colors = variant === 'solid' ? SOLID_COLORS : FIT_COLORS[scoreSection];

  return (
    <Chip
      id={`career-path-pill-${id}`}
      variant="pill"
      size="sm"
      label={name}
      clickable={interactive}
      onClick={interactive ? () => navigate(path) : undefined}
      sx={chipSx(colors, interactive, variant)}
    />
  );
}

const chipSx =
  (colors: PillColors, interactive: boolean, variant: 'fit' | 'solid'): SxProps<Theme> =>
  ({ backrs, typography }) => ({
    backgroundColor: backrs.colors[colors.bg],
    border: `1px solid ${backrs.colors[colors.border]}`,
    justifyContent: 'flex-start',
    width: 'fit-content',
    '& .MuiChip-label': {
      color: backrs.colors[colors.text],
      ...(variant === 'solid' ? typography['text-sm-medium'] : typography['text-xs-medium']),
    },
    cursor: interactive ? 'pointer' : 'default',
    ...(interactive && {
      '&:hover': {
        backgroundColor: backrs.colors[colors.hoverBg],
      },
    }),
  });
